"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
// Locale-aware `Link`: the plain `next/link` emits a locale-LESS path, so
// every card click paid a middleware redirect round-trip.
import { Link } from "@/i18n/routing";
import type { LjArticle } from "@/fixtures/lj/fixtures";
import { ljArticleDate } from "@/fixtures/lj/search-fixtures";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";

/**
 * Search results as a LIST, not as a card grid.
 *
 * They were rendered through `SummaryGrid` — the same three-up card block the
 * category pages use — inside a `max-w-[1280px]` wrapper nested in the grid's
 * own `max-w-[1440px]`, so three cards and an LREC were squeezed into a column
 * narrower than the masthead above them. QA: "change the search result articles
 * as article listing items" and "do not add a width constrain on the container
 * of the articles result section".
 *
 * A row per result: thumbnail, category, headline, byline, divided by a
 * hairline. The whole row is one link, and the headline carries the vertical's
 * underline affordance.
 *
 * Twelve at a time, revealed as the reader reaches the end — QA: "each batch of
 * data should have 12 article items, add scroll to load effect, load more when
 * reaching the end of the list". The sentinel exists ONLY while something
 * remains, so a short result set does not end on a spinner promising more.
 */
const PAGE_SIZE = 12;

export function SearchResultList({ items }: { items: LjArticle[] }) {
  const [visible, setVisible] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // A new query must not inherit the previous one's scroll depth.
  useEffect(() => setVisible(PAGE_SIZE), [items]);

  const shown = items.slice(0, visible);
  const hasMore = visible < items.length;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting)
          setVisible((v) => Math.min(v + PAGE_SIZE, items.length));
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, items.length]);

  return (
    <div className="flex flex-col">
    <ol className="m-0 flex list-none flex-col p-0">
      {shown.map((item, i) => (
        <li
          key={`${item.title}-${i}`}
          data-lj-result-row
          className="py-6 first:pt-0"
          style={{
            borderBottom:
              i < shown.length - 1
                ? `1px solid ${LJ_COLORS.dividerHair}`
                : undefined,
          }}
        >
          <Link
            href={item.href}
            // `items-center`: the text block is shorter than a 4:3 thumbnail,
            // so aligning to the top left it hanging at the picture's shoulder.
            // QA: "make the image and content vertically align center".
            className="group hkej-tap flex items-center gap-5 sm:gap-8"
            style={{ color: LJ_COLORS.textPrimary }}
          >
            <span
              className="relative w-[120px] shrink-0 overflow-hidden sm:w-[220px] lg:w-[280px]"
              style={{
                aspectRatio: 4 / 3,
                backgroundColor: LJ_COLORS.bgSubtle,
              }}
            >
              <Image
                src={item.image}
                alt={item.title}
                fill
                className="object-contain object-center"
                sizes="(max-width: 639px) 120px, (max-width: 1279px) 220px, 280px"
                unoptimized
              />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span
                style={{
                  ...LJ_TYPE.eyebrow,
                  fontWeight: 400,
                  color: LJ_COLORS.textPrimary,
                }}
              >
                {item.category}
              </span>
              <h3
                className="hkej-tap-title line-clamp-2"
                style={{
                  ...LJ_TYPE.cardHed,
                  color: LJ_COLORS.textPrimary,
                  marginTop: 6,
                }}
              >
                {item.title}
              </h3>
              {/* Byline then date, each on its OWN line under the headline —
                  QA: "move date to a separated line". Sharing a row made them
                  read as one credit; the date is the piece's, not the
                  writer's. */}
              {item.author ? (
                <span
                  style={{
                    ...LJ_TYPE.byline,
                    fontWeight: 400,
                    color: LJ_COLORS.textPrimary,
                    marginTop: 12,
                  }}
                >
                  By {item.author}
                </span>
              ) : null}
              {ljArticleDate(item.title) ? (
                <span
                  style={{
                    ...LJ_TYPE.byline,
                    fontWeight: 400,
                    color: LJ_COLORS.textSecondary,
                    marginTop: item.author ? 4 : 12,
                  }}
                >
                  {ljArticleDate(item.title)}
                </span>
              ) : null}
            </div>
          </Link>
        </li>
      ))}
    </ol>

      {hasMore ? (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center py-8"
          aria-hidden="true"
        >
          <span
            className="inline-block h-8 w-8 animate-spin rounded-full border-[2px]"
            style={{
              borderColor: LJ_COLORS.dividerHair,
              borderTopColor: LJ_COLORS.textPrimary,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
